import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REDUX_API_STATUS } from "../../utils/constants.ts";

const { IDLE, LOADING, SUCCESS, FAILED } = REDUX_API_STATUS;

export interface UserState {
  currentAuthenticatedUser: string | null;
  loginStatus: string;
  userClient: string | null;
  userOwner: string | null;
  userIsSuperUser: boolean | null;
  userIsStaff: boolean | null;
  userIsGuest: boolean | null;
  permissions: boolean | null;
  userClientHubUrl?: string | null;
  userClientInsightsUrl?: string | null;
}

const initialState: UserState = {
  currentAuthenticatedUser: null,
  loginStatus: IDLE,
  userClient: null,
  userOwner: null,
  userIsSuperUser: null,
  userIsStaff: null,
  userIsGuest: null,
  permissions: null,
  userClientHubUrl: null,
  userClientInsightsUrl: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCurrentAuthenticatedUser: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.currentAuthenticatedUser = action.payload;
    },
    setLoginStatusSuccess: (state) => {
      state.loginStatus = SUCCESS;
    },
    setLoginStatusLoading: (state) => {
      state.loginStatus = LOADING;
    },
    setLoginStatusFailed: (state) => {
      state.loginStatus = FAILED;
    },
    setLoginStatusIdle: (state) => {
      state.loginStatus = IDLE;
    },
    setUserClient: (state, action: PayloadAction<string | null>) => {
      state.userClient = action.payload;
    },
    setUserOwner: (state, action: PayloadAction<string | null>) => {
      state.userOwner = action.payload;
    },
    setUserIsSuperUser: (state, action: PayloadAction<boolean | null>) => {
      state.userIsSuperUser = action.payload;
    },
    setUserIsStaff: (state, action: PayloadAction<boolean | null>) => {
      state.userIsStaff = action.payload;
    },
    setUserIsGuest: (state, action: PayloadAction<boolean | null>) => {
      state.userIsGuest = action.payload;
    },
    setUserPermissions: (state, action: PayloadAction<boolean | null>) => {
      state.permissions = action.payload;
    },
    setUserClientHubUrl: (state, action: PayloadAction<string | null>) => {
      state.userClientHubUrl = action.payload;
    },
    setUserClientInsightsUrl: (state, action: PayloadAction<string | null>) => {
      state.userClientInsightsUrl = action.payload;
    },
    logoutUser: (state) => {
      state.currentAuthenticatedUser = null;
      state.userClient = null;
      state.userOwner = null;
      state.userIsSuperUser = null;
      state.userIsStaff = null;
      state.userIsGuest = null;
      state.permissions = null;
      // Only remove OUR key. Never localStorage.clear() here: that wiped
      // Amplify's session bookkeeping before Auth.signOut() could run, so
      // tokens were never revoked and Cognito's cookie never cleared —
      // sign-out only looked like sign-out. Amplify cleans up its own keys.
      localStorage.removeItem("accessToken");
      state.loginStatus = IDLE;
      state.userClientHubUrl = null;
      state.userClientInsightsUrl = null;
    },
  },
});

export const {
  setCurrentAuthenticatedUser,
  setLoginStatusLoading,
  setLoginStatusSuccess,
  setLoginStatusFailed,
  setLoginStatusIdle,
  setUserClient,
  setUserOwner,
  setUserIsSuperUser,
  setUserIsStaff,
  setUserIsGuest,
  setUserPermissions,
  setUserClientHubUrl,
  setUserClientInsightsUrl,
  logoutUser,
} = userSlice.actions;

export default userSlice.reducer;
